import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/common/StatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Mail, Phone, Calendar, FileText, Upload, Send, Download, CheckCircle } from "lucide-react";
import CertificateDownload from "@/components/common/CertificateDownload";
import InlineCertificateViewer from "@/components/common/InlineCertificateViewer";
import { LetterDownload, InlineLetterViewer } from "@/components/common/LetterOfGoodStanding";
import InlineDocViewer from "@/components/common/InlineDocViewer";
import { emailMembershipDocs } from "@/lib/membershipDocs";
import {
  DOCUMENT_SECTIONS,
  REQUIRED_DOCUMENT_SLOTS,
  mapDocsToSlots,
} from "@/constants/documents";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { memberNumberOf } from "@/lib/membershipNumber";

// Supabase Storage rejects object keys with spaces/colons etc. ("Invalid key"). Make names safe.
const sanitizeFileName = (name: string) => name.replace(/[^a-zA-Z0-9.\-_]/g, "_").replace(/_+/g, "_");

const STATUS_FLOW = [
  "pending",
  "document_verified",
  "course_assigned",
  "course_in_progress",
  "course_completed",
  "sent_for_approval",
] as const;

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  document_verified: "Documents Verified",
  course_assigned: "Course Assigned",
  course_in_progress: "Course In Progress",
  course_completed: "Course Completed",
  sent_for_approval: "Sent for Approval",
};

interface StudentRow {
  user_id: string;
  full_name: string;
  email: string;
  phone: string | null;
  created_at: string;
  application: any | null;
}

const AdminStudentTable = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState<StudentRow | null>(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applyStudent, setApplyStudent] = useState<StudentRow | null>(null);
  const [applyForm, setApplyForm] = useState({ qualification: "", notes: "", gender: "" });
  const [files, setFiles] = useState<{ file: File; slotId: string; type: string }[]>([]);
  const [applyLoading, setApplyLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [studentDocs, setStudentDocs] = useState<any[]>([]);
  const docsBySlot = useMemo(() => mapDocsToSlots(studentDocs), [studentDocs]);

  const fetchStudents = async () => {
    const { data: studentRoles } = await supabase
      .from("user_roles")
      .select("user_id")
      .eq("role", "student");

    if (!studentRoles || studentRoles.length === 0) {
      setStudents([]);
      setLoading(false);
      return;
    }

    const studentIds = studentRoles.map((r) => r.user_id);

    const { data: profiles } = await supabase
      .from("profiles")
      .select("user_id, full_name, email, phone, created_at")
      .in("user_id", studentIds);

    const { data: applications } = await supabase
      .from("applications")
      .select("*")
      .in("user_id", studentIds);

    const appMap = new Map<string, any>();
    applications?.forEach((a) => {
      if (!appMap.has(a.user_id) || new Date(a.created_at) > new Date(appMap.get(a.user_id).created_at)) {
        appMap.set(a.user_id, a);
      }
    });

    const merged: StudentRow[] = (profiles || []).map((p) => ({
      user_id: p.user_id,
      full_name: p.full_name,
      email: p.email,
      phone: p.phone,
      created_at: p.created_at,
      application: appMap.get(p.user_id) || null,
    }));

    setStudents(merged);
    setLoading(false);
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const openApplyDialog = (student: StudentRow) => {
    setApplyStudent(student);
    setApplyForm({ qualification: "", notes: "", gender: "" });
    setFiles([]);
    setApplyDialogOpen(true);
  };

  const handleSubmitApplication = async () => {
    if (!applyStudent || !user) return;

    const missingDocs = REQUIRED_DOCUMENT_SLOTS
      .filter((slot) => !files.some((f) => f.slotId === slot.id))
      .map((slot) => slot.label);
    if (missingDocs.length) {
      toast({ title: "Missing documents", description: `Please upload: ${missingDocs.join(", ")}.`, variant: "destructive" });
      return;
    }

    setApplyLoading(true);

    const { data: app, error } = await supabase
      .from("applications")
      .insert({
        user_id: applyStudent.user_id,
        full_name: applyStudent.full_name,
        email: applyStudent.email,
        phone: applyStudent.phone || "",
        qualification: applyForm.qualification,
        notes: applyForm.notes,
        gender: applyForm.gender || null,
      })
      .select()
      .single();

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setApplyLoading(false);
      return;
    }

    for (const { file, type } of files) {
      const filePath = `${applyStudent.user_id}/${app.id}/${Date.now()}_${sanitizeFileName(file.name)}`;
      const { error: uploadError } = await supabase.storage
        .from("application-documents")
        .upload(filePath, file);

      if (uploadError) {
        toast({ title: "Upload Failed", description: `Failed to upload ${type}: ${uploadError.message}`, variant: "destructive" });
        continue;
      }

      const { error: docError } = await supabase.from("documents").insert({
        application_id: app.id,
        user_id: applyStudent.user_id,
        document_type: type,
        file_name: file.name,
        file_path: filePath,
      });

      if (docError) {
        toast({ title: "Record Failed", description: `Failed to save ${type} record: ${docError.message}`, variant: "destructive" });
      }
    }

    toast({ title: "Application submitted", description: `Application created for ${applyStudent.full_name}` });
    setApplyDialogOpen(false);
    setApplyLoading(false);
    await fetchStudents();
  };

  const handleStatusChange = async (app: any, newStatus: string) => {
    if (!user) return;
    setStatusLoading(true);

    const { error } = await supabase
      .from("applications")
      .update({ status: newStatus as any, reviewed_by: user.id })
      .eq("id", app.id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setStatusLoading(false);
      return;
    }

    await supabase.from("application_status_history").insert({
      application_id: app.id,
      old_status: app.status,
      new_status: newStatus as any,
      changed_by: user.id,
    });

    toast({ title: "Status updated", description: `Status changed to ${STATUS_LABELS[newStatus] || newStatus}` });
    setStatusLoading(false);

    await fetchStudents();
    if (selectedStudent) {
      const updated = students.find((s) => s.user_id === selectedStudent.user_id);
      if (updated) setSelectedStudent({ ...updated });
    }
  };

  const getNextStatuses = (currentStatus: string): string[] => {
    const idx = STATUS_FLOW.indexOf(currentStatus as any);
    if (idx === -1) return [];
    return STATUS_FLOW.slice(idx + 1) as unknown as string[];
  };

  const filtered = students.filter(
    (s) =>
      s.full_name.toLowerCase().includes(search.toLowerCase()) ||
      s.email.toLowerCase().includes(search.toLowerCase())
  );

  // One file per slot — picking again replaces the previous choice.
  const addFile = (file: File, slotId: string, type: string) => {
    setFiles((prev) => [...prev.filter((f) => f.slotId !== slotId), { file, slotId, type }]);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="rounded-2xl gradient-hero p-8 text-primary-foreground shadow-xl">
          <h2 className="text-2xl font-bold tracking-tight">Student Management</h2>
          <p className="mt-1.5 text-primary-foreground/70 text-sm">View students, submit applications, manage status & documents</p>
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search by name or email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-card/80 backdrop-blur-sm" />
        </div>

        <Card className="glass-premium shadow-lg overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Certificate</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((student) => (
                    <TableRow
                      key={student.user_id}
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => {
                        setSelectedStudent(student);
                        setStudentDocs([]);
                        if (student.application) {
                          supabase
                            .from("documents")
                            .select("*")
                            .eq("application_id", student.application.id)
                            .then(({ data }) => setStudentDocs(data || []));
                        }
                      }}
                    >
                      <TableCell className="font-medium">{student.full_name}</TableCell>
                      <TableCell className="text-muted-foreground">{student.email}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(student.created_at), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell>
                        {student.application ? (
                          student.application.payment_status === "paid" || student.application.status === "certified" ? (
                            <StatusBadge status="paid" />
                          ) : (
                            <StatusBadge status={student.application.status} />
                          )
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">No Application</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {student.application && (student.application.payment_status === "paid" || student.application.status === "certified") ? (
                          <div className="flex gap-1">
                            <InlineCertificateViewer
                              fullName={student.full_name}
                              certId={memberNumberOf(student.application)}
                              date={format(new Date(student.application.updated_at), "MMMM d, yyyy")}
                            />
                            <CertificateDownload
                              fullName={student.full_name}
                              certId={memberNumberOf(student.application)}
                              date={format(new Date(student.application.updated_at), "MMMM d, yyyy")}
                              applicationId={student.application.id}
                            />
                            <InlineLetterViewer
                              fullName={student.full_name}
                              memberNum={memberNumberOf(student.application)}
                              date={format(new Date(student.application.updated_at), "MMMM d, yyyy")}
                              city={student.application.city}
                              country={student.application.country}
                              gender={student.application.gender}
                            />
                            <LetterDownload
                              fullName={student.full_name}
                              memberNum={memberNumberOf(student.application)}
                              date={format(new Date(student.application.updated_at), "MMMM d, yyyy")}
                              city={student.application.city}
                              country={student.application.country}
                              gender={student.application.gender}
                            />
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {!student.application && (
                          <Button
                            size="sm"
                            className="gradient-warm text-white border-0 shadow-sm hover:opacity-90"
                            onClick={(e) => {
                              e.stopPropagation();
                              openApplyDialog(student);
                            }}
                          >
                            <FileText className="mr-1.5 h-3.5 w-3.5" />
                            Apply
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                        <FileText className="mx-auto h-8 w-8 text-muted-foreground/30 mb-2" />
                        No students found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Student Detail Sheet */}
      <Sheet open={!!selectedStudent} onOpenChange={(open) => !open && setSelectedStudent(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          {selectedStudent && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full gradient-warm flex items-center justify-center text-sm font-bold text-white shadow-md">
                    {selectedStudent.full_name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
                  </div>
                  <div>
                    <SheetTitle>{selectedStudent.full_name}</SheetTitle>
                    <SheetDescription>Student details & application status</SheetDescription>
                  </div>
                </div>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                <div className="grid gap-3 p-4 rounded-xl bg-muted/30 border border-border/50">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-primary" />
                    <span>{selectedStudent.email}</span>
                  </div>
                  {selectedStudent.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-primary" />
                      <span>{selectedStudent.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-primary" />
                    <span>Registered {format(new Date(selectedStudent.created_at), "MMM d, yyyy")}</span>
                  </div>
                </div>

                {selectedStudent.application ? (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-foreground">Application</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                        <p className="text-muted-foreground text-xs">App ID</p>
                        <p className="font-mono text-xs mt-1">{selectedStudent.application.id.slice(0, 8)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                        <p className="text-muted-foreground text-xs">Qualification</p>
                        <p className="font-medium text-xs mt-1">{selectedStudent.application.qualification || "—"}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                        <p className="text-muted-foreground text-xs">Status</p>
                        <div className="mt-1"><StatusBadge status={selectedStudent.application.status} /></div>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                        <p className="text-muted-foreground text-xs">Payment</p>
                        <p className="font-medium text-xs mt-1">{selectedStudent.application.payment_status || "unpaid"}</p>
                      </div>
                    </div>
                    {selectedStudent.application.notes && (
                      <div className="text-sm p-3 rounded-lg bg-muted/20 border border-border/30">
                        <p className="text-muted-foreground text-xs">Notes</p>
                        <p className="mt-1">{selectedStudent.application.notes}</p>
                      </div>
                    )}

                    {/* Documents */}
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-foreground">Documents</p>
                      {studentDocs.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Type</TableHead>
                              <TableHead>File Name</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {studentDocs.map((doc) => (
                              <TableRow key={doc.id}>
                                <TableCell className="font-medium text-sm">{doc.document_type}</TableCell>
                                <TableCell className="text-muted-foreground text-xs">{doc.file_name}</TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <InlineDocViewer filePath={doc.file_path} fileName={doc.file_name} />
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={async () => {
                                        const { data } = await supabase.storage
                                          .from("application-documents")
                                          .createSignedUrl(doc.file_path, 3600, { download: true });
                                        if (data?.signedUrl) {
                                          const a = document.createElement("a");
                                          a.href = data.signedUrl;
                                          a.download = doc.file_name;
                                          a.click();
                                        }
                                      }}
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="flex flex-col items-center py-6 text-center border rounded-xl bg-muted/10">
                          <FileText className="h-6 w-6 text-muted-foreground/40 mb-1" />
                          <p className="text-xs text-muted-foreground">No documents uploaded yet</p>
                        </div>
                      )}

                      {/* Upload/Replace */}
                      <div className="space-y-4">
                        <p className="text-xs font-medium text-muted-foreground">Upload / Replace Documents</p>
                        {DOCUMENT_SECTIONS.map((section) => (
                          <div key={section.title} className="space-y-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                              {section.title}
                              {section.slots.every((s) => s.required) && <span className="text-destructive"> *</span>}
                            </p>
                            {section.slots.map((slot) => {
                              const existing = docsBySlot[slot.id];
                              return (
                                <div key={slot.id} className="flex items-center gap-3 rounded-xl border p-3 bg-muted/10 hover:bg-muted/20 transition-colors">
                                  <Upload className="h-4 w-4 text-primary shrink-0" />
                                  <span className="text-sm flex-1 min-w-0">
                                    {slot.label}
                                    {slot.required
                                      ? <span className="text-destructive"> *</span>
                                      : <span className="text-muted-foreground"> (optional)</span>}
                                  </span>
                                  <label className="cursor-pointer shrink-0">
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        e.target.value = "";
                                        if (!file || !selectedStudent?.application || !user) return;
                                        const app = selectedStudent.application;
                                        const filePath = `${selectedStudent.user_id}/${app.id}/${Date.now()}_${sanitizeFileName(file.name)}`;

                                        const { error: uploadError } = await supabase.storage
                                          .from("application-documents")
                                          .upload(filePath, file);

                                        if (uploadError) {
                                          toast({ title: "Upload Failed", description: uploadError.message, variant: "destructive" });
                                          return;
                                        }

                                        if (existing) {
                                          await supabase.storage.from("application-documents").remove([existing.file_path]);
                                          await supabase.from("documents").delete().eq("id", existing.id);
                                        }

                                        const { error: docError } = await supabase.from("documents").insert({
                                          application_id: app.id,
                                          user_id: selectedStudent.user_id,
                                          document_type: slot.type,
                                          file_name: file.name,
                                          file_path: filePath,
                                        });

                                        if (docError) {
                                          toast({ title: "Record Failed", description: docError.message, variant: "destructive" });
                                          return;
                                        }

                                        toast({ title: "Uploaded", description: `${slot.label} uploaded successfully` });
                                        const { data: freshDocs } = await supabase
                                          .from("documents")
                                          .select("*")
                                          .eq("application_id", app.id);
                                        setStudentDocs(freshDocs || []);
                                      }}
                                    />
                                    <span className="text-sm font-medium text-primary hover:underline">
                                      {existing ? "Replace" : "Upload"}
                                    </span>
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>

                    {!["sent_for_approval", "approved", "rejected", "paid", "certified"].includes(selectedStudent.application.status) && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Update Status</Label>
                        <Select
                          disabled={statusLoading}
                          onValueChange={(val) => handleStatusChange(selectedStudent.application, val)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select next status..." />
                          </SelectTrigger>
                          <SelectContent>
                            {getNextStatuses(selectedStudent.application.status).map((s) => (
                              <SelectItem key={s} value={s}>
                                {STATUS_LABELS[s] || s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {selectedStudent.application.status === "course_completed" && (
                      <Button
                        className="w-full gradient-warm text-white border-0 shadow-md"
                        disabled={statusLoading}
                        onClick={() => handleStatusChange(selectedStudent.application, "sent_for_approval")}
                      >
                        <Send className="mr-2 h-4 w-4" />
                        Send for ACA Approval
                      </Button>
                    )}

                    {selectedStudent.application.status === "approved" && selectedStudent.application.payment_status !== "paid" && (
                      <Button
                        className="w-full gradient-warm text-white border-0 shadow-md"
                        disabled={markPaidLoading}
                        onClick={async () => {
                          if (!user) return;
                          setMarkPaidLoading(true);
                          // Select the row back: the membership number is allocated by the
                          // database on this update, so the local copy does not have it yet.
                          const { data: certified, error } = await supabase
                            .from("applications")
                            .update({
                              payment_status: "paid",
                              payment_date: new Date().toISOString(),
                              payment_reference: "manual_admin_" + user.id,
                              status: "paid" as any,
                            })
                            .eq("id", selectedStudent.application.id)
                            .select()
                            .maybeSingle();
                          if (!error) {
                            // Auto-email Certificate + Letter of Good Standing on certification.
                            emailMembershipDocs(selectedStudent.application.id, {
                              fullName: selectedStudent.full_name,
                              certId: memberNumberOf(certified ?? selectedStudent.application),
                              date: format(new Date(), "MMMM d, yyyy"),
                              city: selectedStudent.application.city,
                              country: selectedStudent.application.country,
                            }).catch(() => {});
                            toast({ title: "Marked as Paid", description: `Payment recorded for ${selectedStudent.full_name}. Documents emailed.` });
                            await fetchStudents();
                          } else {
                            toast({ title: "Error", description: error.message, variant: "destructive" });
                          }
                          setMarkPaidLoading(false);
                        }}
                      >
                        <CheckCircle className="mr-2 h-4 w-4" />
                        {markPaidLoading ? "Processing..." : "Mark as Paid"}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
                    <p className="text-sm text-muted-foreground mb-3">No application submitted yet</p>
                    <Button className="gradient-warm text-white border-0" onClick={() => openApplyDialog(selectedStudent)}>
                      <FileText className="mr-2 h-4 w-4" />
                      Submit Application
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Application Submit Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submit Application for {applyStudent?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Qualification</Label>
              <Input
                value={applyForm.qualification}
                onChange={(e) => setApplyForm({ ...applyForm, qualification: e.target.value })}
                placeholder="e.g. B.Com, CA Inter"
              />
            </div>
            <div className="space-y-2">
              {/* Drives the he/she pronoun in the Letter of Good Standing. */}
              <Label>Gender</Label>
              <Select value={applyForm.gender} onValueChange={(v) => setApplyForm({ ...applyForm, gender: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={applyForm.notes}
                onChange={(e) => setApplyForm({ ...applyForm, notes: e.target.value })}
                rows={3}
                placeholder="Additional notes..."
              />
            </div>
            {DOCUMENT_SECTIONS.map((section) => (
              <div key={section.title} className="space-y-3">
                <Label>
                  {section.title}
                  {section.slots.every((s) => s.required) && <span className="text-destructive"> *</span>}
                </Label>
                {section.slots.map((slot) => {
                  const picked = files.find((f) => f.slotId === slot.id);
                  return (
                    <div key={slot.id} className="flex items-center gap-3 rounded-xl border p-3 bg-muted/10">
                      <Upload className="h-5 w-5 text-primary" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          {slot.label}
                          {slot.required
                            ? <span className="text-destructive"> *</span>
                            : <span className="font-normal text-muted-foreground"> (optional)</span>}
                        </p>
                        {picked ? (
                          <p className="text-xs text-success truncate">{picked.file.name}</p>
                        ) : (
                          <p className="text-xs text-muted-foreground">No file selected</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) addFile(file, slot.id, slot.type);
                              e.target.value = "";
                            }}
                          />
                          <span className="text-sm font-medium text-primary hover:underline">
                            {picked ? "Replace" : "Browse"}
                          </span>
                        </label>
                        {picked && !slot.required && (
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-destructive"
                            onClick={() => setFiles((prev) => prev.filter((f) => f.slotId !== slot.id))}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={handleSubmitApplication} disabled={applyLoading} className="w-full gradient-warm text-white border-0">
              {applyLoading ? "Submitting..." : "Submit Application"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminStudentTable;
